/**
 * Per-database installer that wires the action tracer into Yjs and Redux:
 *
 * - Adds a `doc.on('afterTransaction', ...)` listener that, when a trace is
 *   active, calls `markYjsTransactionCommit` (T1). Runs synchronously
 *   inside `doc.transact`, so the active-trace slot is still set.
 *
 * - Adds a *second* `doc.on('update', ...)` listener that fires after the
 *   existing `syncCollabChangesToRedux` listener. Because Yjs invokes
 *   listeners in registration order, this second listener runs after
 *   `dispatch(applyDatabaseDiff(...))` has returned synchronously, giving
 *   us T2.
 *
 * The listeners themselves are extremely small no-ops when there is no
 * active traced action (just a single `activeTrace === null` short-circuit).
 * It is therefore safe to install at database bootstrap.
 */

import { markReduxSyncComplete, markYjsTransactionCommit } from './tracer';

interface PerfTracerYDoc {
  on: (event: 'afterTransaction' | 'update', listener: (...args: any[]) => void) => void;
  off: (event: 'afterTransaction' | 'update', listener: (...args: any[]) => void) => void;
}

interface YTransactionLike {
  changed?: Map<unknown, unknown>;
}

interface PerfTracerCollabManager {
  database: {
    doc: PerfTracerYDoc;
  };
}

export interface PerfTracerHandle {
  /** Remove the installed listeners. Idempotent. */
  uninstall: () => void;
}

const installedHandles = new WeakMap<object, PerfTracerHandle>();

/**
 * Install the tracer's Yjs / Redux observation hooks for one collab
 * manager / document. Returns a handle that can be used to uninstall.
 *
 * Calling this multiple times on the same collab manager is safe — the
 * existing handle is returned and no duplicate listeners are added.
 */
export function installPerfTracerForDatabase(
  collabManager: PerfTracerCollabManager,
  contentId: string
): PerfTracerHandle {
  const existing = installedHandles.get(collabManager);
  if (existing) {
    return existing;
  }

  const doc = collabManager.database.doc;

  const onAfterTransaction = (transaction: YTransactionLike) => {
    const changeCount = transaction.changed ? transaction.changed.size : 0;
    markYjsTransactionCommit(changeCount, contentId);
  };

  const onUpdateAfterRedux = () => {
    markReduxSyncComplete();
  };

  doc.on('afterTransaction', onAfterTransaction);
  doc.on('update', onUpdateAfterRedux);

  const handle: PerfTracerHandle = {
    uninstall: () => {
      doc.off('afterTransaction', onAfterTransaction);
      doc.off('update', onUpdateAfterRedux);
      installedHandles.delete(collabManager);
    }
  };
  installedHandles.set(collabManager, handle);
  return handle;
}
